import RAPIER from '@dimforge/rapier3d-compat';
import { PLAYER_GROUPS } from './Layers';
import type { PhysicsWorld } from './PhysicsWorld';

export const CAPSULE_RADIUS = 0.4;
export const CAPSULE_HEIGHT = 1.8;
export const SNEAK_HEIGHT = 1.05;
/** Rapier half-height is the cylinder only: total h = 2 * half + 2 * radius. */
export const CAPSULE_HALF_HEIGHT = (CAPSULE_HEIGHT - CAPSULE_RADIUS * 2) / 2;
export const SNEAK_HALF_HEIGHT = (SNEAK_HEIGHT - CAPSULE_RADIUS * 2) / 2;
export const CAPSULE_CENTER_Y = CAPSULE_HALF_HEIGHT + CAPSULE_RADIUS;
export const SNEAK_CENTER_Y = SNEAK_HALF_HEIGHT + CAPSULE_RADIUS;

export const KCC_OFFSET = 0.02;
export const AUTOSTEP_MAX = 0.4;
export const AUTOSTEP_MIN_WIDTH = 0.2;
const SNAP_DISTANCE = 0.35;
const MAX_SLOPE = (50 * Math.PI) / 180;
/** Rest center sits on the KCC offset so the first query is not start-in-contact. */
export const CAPSULE_REST_Y = CAPSULE_CENTER_Y + KCC_OFFSET;

export class CharacterMotor {
  readonly body: RAPIER.RigidBody;
  readonly collider: RAPIER.Collider;
  readonly controller: RAPIER.KinematicCharacterController;
  private readonly physics: PhysicsWorld;
  private disposed = false;
  private queried = false;
  private sneaking = false;

  constructor(physics: PhysicsWorld, x = 0, y = CAPSULE_REST_Y, z = 0) {
    this.physics = physics;
    this.body = physics.world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(x, y, z).lockRotations(),
    );
    this.collider = physics.world.createCollider(
      RAPIER.ColliderDesc.capsule(CAPSULE_HALF_HEIGHT, CAPSULE_RADIUS).setCollisionGroups(
        PLAYER_GROUPS,
      ),
      this.body,
    );

    this.controller = physics.world.createCharacterController(KCC_OFFSET);
    this.controller.setUp({ x: 0, y: 1, z: 0 });
    this.controller.setSlideEnabled(true);
    this.controller.enableAutostep(AUTOSTEP_MAX, AUTOSTEP_MIN_WIDTH, false);
    this.controller.enableSnapToGround(SNAP_DISTANCE);
    this.controller.setMaxSlopeClimbAngle(MAX_SLOPE);
    this.controller.setMinSlopeSlideAngle(MAX_SLOPE + (5 * Math.PI) / 180);
  }

  get centerY(): number {
    return this.sneaking ? SNEAK_CENTER_Y : CAPSULE_CENTER_Y;
  }

  get height(): number {
    return this.sneaking ? SNEAK_HEIGHT : CAPSULE_HEIGHT;
  }

  /**
   * @param vy vertical velocity used only for snap policy. Snap is off while falling
   * (`vy < -1`) or when the last query was not grounded.
   */
  move(dx: number, dy: number, dz: number, vy = 0): RAPIER.Vector {
    this.applySnapPolicy(vy);
    this.controller.computeColliderMovement(this.collider, { x: dx, y: dy, z: dz });
    this.queried = true;
    const movement = this.controller.computedMovement();
    const pos = this.body.translation();
    this.body.setNextKinematicTranslation({
      x: pos.x + movement.x,
      y: pos.y + movement.y,
      z: pos.z + movement.z,
    });
    return movement;
  }

  get grounded(): boolean {
    return this.controller.computedGrounded();
  }

  translation(): RAPIER.Vector {
    return this.body.translation();
  }

  setTranslation(x: number, y: number, z: number): void {
    this.body.setTranslation({ x, y, z }, true);
    this.body.setNextKinematicTranslation({ x, y, z });
  }

  /** Shrink the capsule in place so feet stay on the slab. */
  setSneak(sneak: boolean): void {
    if (sneak === this.sneaking || this.disposed) {
      return;
    }
    const oldCenter = this.centerY;
    this.sneaking = sneak;
    this.collider.setHalfHeight(sneak ? SNEAK_HALF_HEIGHT : CAPSULE_HALF_HEIGHT);
    const pos = this.body.translation();
    this.setTranslation(pos.x, pos.y + (this.centerY - oldCenter), pos.z);
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.physics.world.removeCharacterController(this.controller);
  }

  private applySnapPolicy(vy: number): void {
    const airborne = this.queried && !this.controller.computedGrounded();
    if (vy < -1 || airborne) {
      this.controller.disableSnapToGround();
    } else {
      this.controller.enableSnapToGround(SNAP_DISTANCE);
    }
  }
}
